import React from 'react';
import styled, { keyframes } from 'styled-components';
import { GoogleLogin } from "@react-oauth/google";
import { X, Layers } from 'lucide-react';
import apiService from '../services/apiService';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(5px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalCard = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  width: 100%;
  max-width: 400px;
  border-radius: 20px;
  padding: 32px;
  position: relative;
  animation: ${fadeIn} 0.2s ease-out;
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0; /* Reset global button margin */
  box-shadow: none;

  &:hover {
    color: #fff;
    background: rgba(255,255,255,0.1);
    transform: none;
  }
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #e50914, #ff4f4f);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 10px 20px rgba(229, 9, 20, 0.3);
  color: white;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 12px;
`;

const Description = styled.p`
  color: #a3a3a3;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 32px;
`;

const GuestButton = styled.button`
  width: 100%;
  padding: 10px 0;
  background: transparent;
  border: 1px solid #333;
  border-radius: 20px; /* Match Google Button pill shape roughly */
  color: #888;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    border-color: #555;
  }
`;

// Simple JWT decoder helper
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) { return null; }
}

const LoginModal = ({ onClose, onLoginSuccess }) => {
  const handleGoogleSuccess = (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) return;

    // 1. Backend Verification
    apiService.loginWithGoogle(idToken)
      .then(response => {
        if (response.success) {
          const user = { ...response.user, rawIdToken: idToken };
          localStorage.setItem("user", JSON.stringify(user));
          if (onLoginSuccess) onLoginSuccess(user);
          onClose(); // Close modal
        } else {
          alert("Login Failed: " + response.message);
        }
      })
      .catch(err => {
        console.error("Login Error:", err);
      });
  };

  return (
    <Overlay onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <ModalCard>
        <CloseButton onClick={onClose}><X size={20} /></CloseButton>

        <IconWrapper>
          <Layers size={32} strokeWidth={2.5} />
        </IconWrapper>

        <Title>학습 기록 저장</Title>
        <Description>
          로그인하여 단어를 저장하고<br />
          나만의 학습 기록을 남겨보세요.
        </Description>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => alert('Login Failed')}
            width="100%"
            theme="filled_black"
            shape="pill"
            text="signin_with"
            locale="ko"
          />

          <GuestButton onClick={() => {
            const guestUser = {
              id: 'guest-' + Date.now(),
              name: 'Guest',
              email: 'guest@nureum.com',
              isGuest: true
            };
            localStorage.setItem('user', JSON.stringify(guestUser));
            localStorage.setItem('guestLastLogin', new Date().toISOString());

            if (onLoginSuccess) onLoginSuccess(guestUser);
            onClose();
          }}>
            비회원으로 체험하기
          </GuestButton>
        </div>
      </ModalCard>
    </Overlay>
  );
};

export default LoginModal;
